import React, { Key, useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./index.less";
import { Dropdown, Input, InputRef, MenuProps, Tooltip, Tour, Tree, TreeDataNode } from "antd";
import type { TourProps } from "antd";
import Icon from "@ant-design/icons";
import {
    SettingSVG,
    AddMemberSVG,
    AddChannelSVG,
    TreeSwitcherSVG,
} from "../../SvgIconCom/ChannelMenuIcon";
import { ChannelItem, globalStore, useChannelRoomStore, useGlobalStore } from "@obsbot/remo-stores";
import { Trans, useTranslate } from "@obsbot/remo-i18n";
import { debounce } from "lodash-es";
import { EmptyChannelMenu } from "../EmptyChannelMenu";
import { eventBus, filterInputValue, limitInputLength } from "@obsbot/remo-utils";
import { ConfirmEditModal } from "../../ConfirmEditModal";
import ChannelGuideIconSVG from "../channelGuideIcon.svg";
import { useClickAway } from "react-use";

type ChannelItemType = Pick<ChannelItem, "channelName" | "channelUuid" | "inviteFlag">;
export * from "../../SvgIconCom/ChannelMenuIcon";
export interface ChannelMenuProps {
    myChannels: ChannelItemType[];
    joinedChannels: ChannelItemType[];
    activeChannel: ChannelItem | null;
    onInviteMember?: (channelUUID: string, inviteFlag: string) => void;
    onChannelSetting?: (channel: Omit<ChannelItemType, "inviteFlag">) => void;
    onEditChannel?: (channel: Omit<ChannelItemType, "inviteFlag">) => void; // 修改频道设置
    onCreateChannel?: () => void;
    onChangeActiveChannel?: (channelUUID: string) => void;
    onHandleJoinChannel?: (channel: ChannelItem) => void;
    onMenuSelect?: (
        key:
            | "editChannel"
            | "delChannel"
            | "quitChannel"
            | "delJoinChannel"
            | "inviteFriends"
            | "settingChannel",
        channelId: string,
        channelName: string,
    ) => void;
}

type ChannelType = "myChannels" | "joinedChannels"; // 定义频道类型的联合类型
export const ChannelMenu: React.FC<ChannelMenuProps> = ({
    myChannels = [],
    joinedChannels = [],
    activeChannel,
    onChannelSetting,
    onEditChannel,
    onInviteMember,
    onCreateChannel,
    onChangeActiveChannel,
    onHandleJoinChannel,
    onMenuSelect,
}) => {
    const { isJoinedRTC, userUUID } = useChannelRoomStore();
    const { needShowGuide } = useGlobalStore();
    const t = useTranslate();
    const rightMenuObject = useRef<Record<string, any>>({
        myChannels: [
            {
                label: t("channel.edit"),
                key: "editChannel",
            },
            {
                label: t("channel.setting"),
                key: "settingChannel",
            },
            {
                label: t("channel.inviteFriends"),
                key: "inviteFriends",
            },
            {
                type: "divider",
            },
            {
                label: t("channel.delete"),
                key: "delChannel",
            },
        ],
        joinedChannels: [
            {
                label: t("channel.quit"),
                key: "quitChannel",
            },
            {
                label: t("channel.delete"),
                key: "delJoinChannel",
            },
        ],
    });
    const [selectedKeys, setSelectedKeys] = useState<Key[]>(
        myChannels.length > 0 ? [myChannels[0].channelUuid] : [],
    ); // 明确指定类型
    const [showRightMenu, setShowRightMenu] = useState(false); // 控制菜单的显示与隐藏
    const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 }); // 菜单位置
    const [rightMenuType, setRightMenuType] = useState<ChannelType>("myChannels");
    // 当前操作的频道
    const [actionChannelId, setActionChannelId] = useState<string>("");
    // expand的状态
    const [expand, setExpand] = useState({
        myChannels: true,
        joinedChannels: true,
    });
    // 正在修改名称的频道
    const [editingChannelId, setEditingChannelId] = useState<string>("");
    const [editNewChannelName, setEditNewChannelName] = useState<string>("");
    // @ts-ignore
    const treeRef = useRef<AntdTree<TreeDataNode>>(null);
    // 是否提示用户保存
    const [promptSaveName, setPromptSaveName] = useState("");
    const isShowPromptSave = useMemo(() => promptSaveName !== "", [promptSaveName]);
    // 第一个频道元素
    const firstChannelRef = useRef<HTMLDivElement>(null);
    // 是否加载完毕
    const [isLoaded, setIsLoaded] = useState(false);
    // 是否显示引导
    const [isShowGuideChannel, setIsShowGuideChannel] = useState(false);
    // 点击区域的ref
    const allowClickRef = useRef<HTMLDivElement>(null);
    const clickWhitelist = [
        "ant-tour-description",
        "guide-title",
        "channel-guide-special",
        "guide-image",
    ];
    useClickAway(allowClickRef, e => {
        const el = e.target as HTMLElement;
        if (!clickWhitelist.some(allowed => el.classList.contains(allowed))) {
            globalStore.updateNeedShowGuide({ channel: false });
            setIsShowGuideChannel(false);
        }
    });

    const handleSelect = debounce((selectedKeys: Key[]): void => {
        if (editingChannelId) {
            return;
        }
        if (selectedKeys.includes("myChannels") || selectedKeys.includes("joinedChannels")) {
            // 获取当前点击的分组key
            const clickedGroup = selectedKeys.includes("myChannels")
                ? "myChannels"
                : "joinedChannels";
            const newExpand = {
                ...expand,
                [clickedGroup]: !expand[clickedGroup],
            };
            setExpand(newExpand);
            const expandedKeys = Object.entries(newExpand)
                .filter(([_, isExpanded]) => isExpanded)
                .map(([key]) => key);
            treeRef.current?.setExpandedKeys(expandedKeys);
            return;
        }
        const isJoinedRTC = useChannelRoomStore.getState().isJoinedRTC;
        if (isJoinedRTC) {
            // setSelectedKeys([activeChannel.channelUUID]);
            return;
        }
        setSelectedKeys(selectedKeys);
        onChangeActiveChannel?.(selectedKeys[0] as string);
    }, 100);

    const setMenuObject = useCallback(
        (hasQuit = true): Record<string, any> => {
            const joinedChannels = [
                {
                    label: t("channel.quit"),
                    key: "quitChannel",
                },
                {
                    label: t("channel.delete"),
                    key: "delJoinChannel",
                },
            ];
            if (!hasQuit) {
                joinedChannels.shift();
            }
            return {
                myChannels: [
                    {
                        label: t("channel.edit"),
                        key: "editChannel",
                    },
                    {
                        label: t("channel.setting"),
                        key: "settingChannel",
                    },
                    {
                        label: t("channel.inviteFriends"),
                        key: "inviteFriends",
                    },
                    {
                        type: "divider",
                    },
                    {
                        label: t("channel.delete"),
                        key: "delChannel",
                    },
                ],
                joinedChannels,
            };
        },
        [t],
    );

    useEffect(() => {
        setTimeout(() => {
            rightMenuObject.current = setMenuObject(
                isJoinedRTC && activeChannel?.channelUuid === actionChannelId,
            );
        }, 0);
    }, [isJoinedRTC, t, activeChannel, actionChannelId, setMenuObject]);

    useEffect(() => {
        if (activeChannel) {
            setSelectedKeys([activeChannel.channelUuid]);
            const expandedKey = ["myChannels"];
            if (activeChannel.ownerUuid !== userUUID || joinedChannels.length > 0) {
                expandedKey.push("joinedChannels");
                setExpand(expand => ({
                    ...expand,
                    joinedChannels: true,
                }));
            }
            treeRef.current?.setExpandedKeys(expandedKey);
        }
    }, [activeChannel, userUUID]);

    // 右键菜单
    const handleRightClick = ({ event, node }: { event: any; node: any }): void => {
        event.preventDefault(); // 阻止默认行为，防止浏览器的右键菜单显示
        if (node.isLeaf && node.key !== "emptyMyChannels") {
            setActionChannelId(node.key);
            rightMenuObject.current = setMenuObject(
                node.key === activeChannel?.channelUuid && isJoinedRTC,
            );
            setMenuPosition({ x: event.clientX - 80, y: event.clientY - 36 });
            const nodeParent = node.pos.split("-")[1] === "0" ? "myChannels" : "joinedChannels"; // 父节点的 key 值
            setRightMenuType(nodeParent as ChannelType);
            setShowRightMenu(true);
        }
    };

    const handleMenuSelect = ({ key }: { key: string }): void => {
        setShowRightMenu(false); // 隐藏菜单
        if (key === "editChannel") {
            setEditingChannelId(actionChannelId);
            setEditNewChannelName(
                myChannels.find(channel => channel.channelUuid === actionChannelId)?.channelName ??
                    "",
            );
            return;
        }
        if (key === "quitChannel" && !isJoinedRTC) {
            return;
        }
        onMenuSelect &&
            onMenuSelect(
                key as "editChannel" | "delChannel" | "quitChannel" | "delJoinChannel",
                actionChannelId,
                myChannels.find(channel => channel.channelUuid === actionChannelId)?.channelName ??
                    "",
            );
    };

    const handleExpand = (expandedKeys: React.Key[]): void => {
        setExpand({
            myChannels: expandedKeys.includes("myChannels") ? true : false,
            joinedChannels: expandedKeys.includes("joinedChannels") ? true : false,
        });
    };

    useEffect(() => {
        setTimeout(() => {
            if (needShowGuide.channel) {
                setIsLoaded(true);
            }
        }, 500);
    }, [needShowGuide.channel]);

    useEffect(() => {
        setIsShowGuideChannel(needShowGuide.channel);
    }, [needShowGuide.channel]);

    const treeData: TreeDataNode[] = useMemo(() => {
        const myChannelsChildren =
            myChannels.length === 0
                ? [
                      {
                          title: <EmptyChannelMenu />,
                          key: "emptyMyChannels",
                          isLeaf: true,
                          icon: null,
                          disabled: true,
                      },
                  ]
                : [
                      ...myChannels.map((channel, idx) => ({
                          title: (
                              <div
                                  className={
                                      "channel-items" + (idx === 0 ? " first-created-channel" : "")
                                  }
                                  onDoubleClick={() => {
                                      handleSelect([channel.channelUuid]);
                                      onHandleJoinChannel &&
                                          onHandleJoinChannel(channel as ChannelItem);
                                  }}
                              >
                                  {channel.channelUuid === editingChannelId ? (
                                      <EditChannelInput
                                          key={channel.channelUuid}
                                          channel={channel}
                                          newChannelName={editNewChannelName}
                                          setEditingChannelId={setEditingChannelId}
                                          setPromptSave={(newChannelName: string) => {
                                              setPromptSaveName(newChannelName);
                                          }}
                                          onChannelSetting={onChannelSetting}
                                      />
                                  ) : (
                                      <>
                                          <Tooltip
                                              placement="bottomLeft"
                                              rootClassName="channel-items-tooltip"
                                              title={channel.channelName}
                                          >
                                              <span>{channel.channelName}</span>
                                          </Tooltip>
                                          <div className="settingAndInvite">
                                              <Tooltip
                                                  placement="bottom"
                                                  rootClassName="channel-items-icon-tooltip"
                                                  title={t("setting")}
                                              >
                                                  <Icon
                                                      component={SettingSVG}
                                                      onClick={e => {
                                                          e.stopPropagation();
                                                          setShowRightMenu(false);
                                                          onEditChannel?.({
                                                              channelName: channel.channelName,
                                                              channelUuid: channel.channelUuid,
                                                          });
                                                      }}
                                                  />
                                              </Tooltip>
                                              <Tooltip
                                                  placement="bottom"
                                                  rootClassName="channel-items-icon-tooltip"
                                                  title={t("invite")}
                                              >
                                                  <Icon
                                                      component={AddMemberSVG}
                                                      onClick={e => {
                                                          e.stopPropagation();
                                                          onInviteMember?.(
                                                              channel.channelUuid,
                                                              channel.inviteFlag,
                                                          );
                                                      }}
                                                  />
                                              </Tooltip>
                                          </div>
                                      </>
                                  )}
                              </div>
                          ),
                          key: channel.channelUuid,
                          icon: <div className="channel-icon-item"></div>,
                          isLeaf: true,
                      })),
                  ];
        const joinedChannelsChildren = joinedChannels.map(channel => ({
            title: (
                <div
                    className="channel-items"
                    onDoubleClick={() => {
                        handleSelect([channel.channelUuid]);
                        onHandleJoinChannel && onHandleJoinChannel(channel as ChannelItem);
                    }}
                >
                    <Tooltip
                        placement="bottomLeft"
                        rootClassName="channel-items-tooltip"
                        title={channel.channelName}
                    >
                        <span>{channel.channelName}</span>
                    </Tooltip>
                </div>
            ),
            key: channel.channelUuid,
            icon: <div className="channel-icon-item"></div>,
            isLeaf: true,
        }));
        const result = [
            {
                title: (
                    <div ref={firstChannelRef} className="default-channel">
                        {t("channel.mine")}
                        <Tooltip
                            arrow={false}
                            getPopupContainer={() => document.body}
                            placement="left"
                            title={t("channel.createChannel")}
                        >
                            <div className="add-channel-btn">
                                <Icon
                                    component={AddChannelSVG}
                                    onClick={e => {
                                        e.stopPropagation();
                                        onCreateChannel && onCreateChannel();
                                    }}
                                />
                            </div>
                        </Tooltip>
                    </div>
                ),
                key: "myChannels",
                selectable: true,
                isLeaf: false,
                autoExpandParent: true,
                children: myChannelsChildren,
            },
        ];
        if (joinedChannelsChildren.length > 0) {
            result.push({
                title: <div className="add-channel">{t("channel.joined")}</div>,
                key: "joinedChannels",
                isLeaf: false,
                selectable: true,
                autoExpandParent: joinedChannels.length > 0,
                children: joinedChannelsChildren,
            });
        }
        return result;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [myChannels, joinedChannels, activeChannel, editingChannelId, editNewChannelName]);

    // 新手引导
    const guideStep: TourProps["steps"] = [
        {
            title: (
                <div className="guide-top">
                    <div className="guide-picture">
                        <img className="guide-image" src={ChannelGuideIconSVG} />
                    </div>
                    <div className="guide-title">{t("channel-guide-title")}</div>
                </div>
            ),
            placement: "right",
            arrow: true,
            description: (
                <Trans
                    components={{
                        join: <span className="channel-guide-special" />,
                    }}
                    i18nKey="channel-guide-description"
                />
            ),
            target: firstChannelRef.current?.parentElement?.parentElement,
        },
    ];

    const handlePopupAlign = (popupDomNode: HTMLElement): void => {
        popupDomNode.style.left = "316px";
        popupDomNode.style.top = "30px";
    };

    const isShowTour = useMemo(
        () =>
            (!!myChannels.length && isShowGuideChannel && isLoaded && needShowGuide.channel) ||
            (!!joinedChannels.length &&
                isShowGuideChannel &&
                isLoaded &&
                needShowGuide.channel &&
                needShowGuide.isToJoined),
        [
            isLoaded,
            needShowGuide.channel,
            needShowGuide.isToJoined,
            isShowGuideChannel,
            myChannels,
            joinedChannels,
        ],
    );

    return (
        <div
            className="channel-menu"
            style={{ height: isJoinedRTC ? "calc(100% - 120px)" : "calc(100% - 60px)" }}
        >
            <Dropdown
                menu={{
                    items: rightMenuObject.current[rightMenuType] as MenuProps["items"],
                    onClick: handleMenuSelect,
                    onMouseLeave: () => {
                        setShowRightMenu(false); // 鼠标离开菜单时隐藏菜单
                    },
                }}
                open={showRightMenu} // 控制菜单的显示与隐藏
                overlayClassName={`channel-menu-dropdown ${rightMenuType}`}
                overlayStyle={{ position: "absolute", top: menuPosition.y, left: menuPosition.x }} // 动态设置菜单的位置
                trigger={["contextMenu"]}
                onOpenChange={open => {
                    setShowRightMenu(open);
                }}
            />
            <Tree
                ref={treeRef}
                blockNode={true}
                defaultExpandAll={true}
                filterTreeNode={node => (node.isLeaf ?? false) && node.key === editingChannelId}
                rootClassName={`channel-menu-tree-list ${editingChannelId ? "editing-channel" : ""} ${isShowTour ? "channel-guide-show" : ""}`}
                selectedKeys={selectedKeys}
                showIcon={true}
                switcherIcon={<TreeSwitcherSVG />}
                treeData={treeData}
                onExpand={expandedKeys => handleExpand(expandedKeys)}
                onRightClick={handleRightClick}
                onSelect={handleSelect}
            />
            <ConfirmEditModal
                content={t("channel.confirmSave")}
                setVisible={() => setPromptSaveName("")}
                visible={isShowPromptSave}
                onCancel={() => {
                    setPromptSaveName("");
                    setEditNewChannelName("");
                    setEditingChannelId("");
                }}
                onConfirm={() => {
                    onChannelSetting?.({
                        channelUuid: editingChannelId,
                        channelName: promptSaveName,
                    });
                    setPromptSaveName("");
                    setEditNewChannelName("");
                    setEditingChannelId("");
                }}
            />
            <Tour
                actionsRender={() => (
                    <div ref={allowClickRef} className="tour-guide-action-buttons">
                        <div
                            className="tour-guide-action-next"
                            onClick={() => {
                                globalStore.updateNeedShowGuide({ channel: false });
                                setIsShowGuideChannel(false);
                            }}
                        >
                            <span>{t("copy-that")}</span>
                        </div>
                        <div
                            className="tour-guide-action-skip"
                            onClick={() => {
                                globalStore.updateNeedShowGuide({
                                    channel: false,
                                    specialEffect: false,
                                    isToJoined: false,
                                });
                                setIsShowGuideChannel(false);
                            }}
                        >
                            {t("skip-all-guide")}
                        </div>
                    </div>
                )}
                closable={false}
                gap={{ radius: 10, offset: 6 }}
                mask={false}
                open={isShowTour}
                rootClassName="custom-tour-guide"
                steps={guideStep}
                onPopupAlign={handlePopupAlign}
            />
        </div>
    );
};

interface EditChannelInputProps {
    channel: ChannelItemType;
    newChannelName: string;
    setPromptSave: (newChannelName: string) => void;
    setEditingChannelId: (channelId: string) => void;
    onChannelSetting?: (channel: Omit<ChannelItemType, "inviteFlag">) => void;
}

const EditChannelInput: React.FC<EditChannelInputProps> = ({
    newChannelName,
    channel,
    setPromptSave,
    setEditingChannelId,
    onChannelSetting,
}): React.ReactElement => {
    const inputRef = useRef<InputRef | null>(null);
    useEffect(() => {
        const inputEl = inputRef.current;
        if (inputEl) {
            inputEl.setSelectionRange(0, newChannelName.length);
        }
    }, []);
    const t = useTranslate();
    const [editNewChannelName, setEditNewChannelName] = useState<string>(newChannelName);
    const isComposing = useRef(false);
    return (
        <Input
            ref={inputRef}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus={true}
            maxLength={16}
            value={editNewChannelName}
            onBlur={() => {
                if (editNewChannelName === channel.channelName) {
                    setEditingChannelId("");
                    setEditNewChannelName("");
                } else {
                    if (!editNewChannelName.trim().length) {
                        eventBus.emit("show-message", {
                            message: t("noInputSingle", { text1: t("channelName") }),
                            type: "error",
                        });
                        // message?.error(t("noInputSingle", { text1: t("channelName") }));
                        setEditingChannelId("");
                        setEditNewChannelName("");
                        return;
                    }
                    setPromptSave(editNewChannelName);
                    return;
                }
            }}
            onChange={e => {
                let filterVal = filterInputValue(e.target.value);
                if (!isComposing.current) {
                    const { val, overLimit } = limitInputLength(filterVal);
                    if (overLimit) {
                        eventBus.emit("show-message", {
                            message: t("nameLengthOverLimit"),
                            type: "warning",
                        });
                    }
                    filterVal = val;
                }
                setEditNewChannelName(filterVal);
            }}
            onCompositionEnd={e => {
                isComposing.current = false;
                const finalValue = (e.target as any).value;
                const { val, overLimit } = limitInputLength(finalValue);
                if (overLimit) {
                    eventBus.emit("show-message", {
                        message: t("nameLengthOverLimit"),
                        type: "warning",
                    });
                }
                setEditNewChannelName(val);
            }}
            onCompositionStart={() => (isComposing.current = true)}
            onKeyDown={e => {
                if (e.key === "Enter") {
                    if (!editNewChannelName.trim().length) {
                        eventBus.emit("show-message", {
                            message: t("noInputSingle", { text1: t("channelName") }),
                            type: "error",
                        });
                        // message?.error(t("noInputSingle", { text1: t("channelName") }));
                        return;
                    }
                    setEditingChannelId("");
                    if (editNewChannelName === channel.channelName) {
                        return;
                    }
                    onChannelSetting?.({
                        ...channel,
                        channelName: editNewChannelName,
                    });
                    setEditNewChannelName("");
                }
            }}
        />
    );
};
